<template>
  <div class="w-full flex flex-col rounded-md shadow-md border p-1">
<!-- Primary row — title cluster (number + name + customer) on the left, badges + actions anchored right -->
    <div class="w-full flex items-center flex-nowrap">
<!-- Title cluster — wraps internally; flex-grow absorbs slack so the right-anchored cluster stays in place -->
      <div class="flex-grow min-w-0 flex flex-row flex-wrap items-center gap-x-3 gap-y-0.5 px-1">
        <div class="w-[70ch] text-left whitespace-normal break-words p-0.5 hover:opacity-70 cursor-pointer" :class="project.isOpen ? 'text-green' : 'text-red'" @click="$emit('open', project._id)">
          {{ titleLabel }}
        </div>
        <div v-if="project.projectClientId?.name" class="w-[50ch] text-left whitespace-normal break-words p-0.5">
          {{ project.projectClientId.name }}
        </div>
      </div>
<!-- Right-anchored cluster -->
      <div class="w-auto flex flex-row flex-nowrap items-center gap-1 flex-shrink-0 px-1">
        <div v-if="project.originalProjectId" class="px-1 text-[10px] font-semibold text-orange flex-shrink-0">CO</div>
        <div v-if="project.isCustomerHold" class="chip-sky flex-shrink-0">Customer Stock</div>
        <div v-if="project.isCustomerReceivingHold" class="chip-sky flex-shrink-0">Customer Hold</div>
        <div v-if="project.isReceivingHold" class="chip-sky flex-shrink-0">Receiving Hold</div>
        <div v-if="closedDateLabel" class="w-auto text-red text-xs flex-shrink-0">{{ closedDateLabel }}</div>
        <div class="flex-shrink-0">
          <button v-if="showChangeOrder" class="bn-text" @click="$emit('addChangeOrder', project._id)">
            <PlusIcon />
            <div>Change Order</div>
          </button>
        </div>
      </div>
    </div>
<!-- Address sub-row — uses shared DisplayAddress component -->
    <div v-if="hasAddress" class="w-full px-1 py-0.5">
      <DisplayAddress :address="project.projectAddress" horizontal googleLink />
    </div>
<!-- Financials sub-row — server only sends services + sales when canSeeFinancials -->
    <div v-if="canSeeFinancials && Array.isArray(project.services) && project.services.length" class="w-full flex flex-row flex-wrap items-center gap-x-3 gap-y-0.5 px-2 py-0.5 border-t">
      <div class="w-auto">{{ showAsUSCurrency(project.sales || 0) }}</div>
      <div class="w-auto text-xs">{{ project.services.length }} {{ project.services.length === 1 ? 'service' : 'services' }}</div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { format, isValid, parseISO } from 'date-fns'
import { showAsUSCurrency } from '@/shared'
import {PlusIcon } from '@heroicons/vue/solid'
import DisplayAddress from '@/components/ui/DisplayAddress.vue'

export default {
  components: {PlusIcon, DisplayAddress },
  props: {
    project: { type: Object, required: true },
    canChangeOrder: { type: Boolean, default: false },
    canSeeFinancials: { type: Boolean, default: false }
  },
  emits: ['open', 'addChangeOrder'],
  setup(props) {
  // Computed
    const titleLabel = computed(() => {
      const p = props.project
      const numbers = p.clientProjectNumber
        ? `${p.companyProjectNumber} / ${p.clientProjectNumber}`
        : p.companyProjectNumber
      const display = p.nickName || p.name || ''
      return display ? `${numbers} - ${display}` : numbers
    })

    const hasAddress = computed(() => {
      const a = props.project?.projectAddress
      return !!a && typeof a === 'object' && Object.keys(a).length > 0
    })

    const closedDateLabel = computed(() => {
      const v = props.project?.closedOn
      if (!v) return ''
      const d = typeof v === 'string' ? parseISO(v) : new Date(v)
      return isValid(d) ? format(d, 'MMM d, yyyy') : ''
    })

    // Hide change-order on existing CO children, on hold states (customer or receiving), and on
    // fulfillment order-projects (an SO is not an install project — no change orders).
    // Change orders belong to install projects only. Warehouse, fulfillment and order projects are
    // operational containers and the server rejects the write — mirrors isWarehouseTypeProject in the
    // backend's shared/projectTypes.
    const showChangeOrder = computed(() =>
      props.canChangeOrder
      && !props.project.originalProjectId
      && !props.project.isCustomerHold
      && !props.project.isCustomerReceivingHold
      && !props.project.isReceivingHold
      && !props.project.isInternalInventory
      && !props.project.isFulfillment
      && !props.project.isFulfillmentOrder
    )

    return { titleLabel, hasAddress, closedDateLabel, showChangeOrder, showAsUSCurrency }
  }
}
</script>
